import { type AxiosInstance } from "axios";
import { format } from "date-fns";
import { getCompletePropertyRows } from "@/utils/propertyRows";
import { type PropertyLocationRow } from "@/types/propertyLocationRow";
import { DOCUMENT_TYPE_LABELS } from "../constants/priceMaster";
import {
  type DocumentType,
  type EstimateResult,
  type ResearchAccess,
  type ResearchRequestFormData,
} from "../types";
import { removePrefectureFromAddress } from "../utils/address";
import { type DeliverySchedule } from "../utils/deliverySchedule";

const documentTypeOrder = Object.keys(DOCUMENT_TYPE_LABELS) as DocumentType[];

export interface RealEstateResearchRequestResponse {
  requestId: string;
}

interface SubmitInput {
  data: ResearchRequestFormData;
  files: File[];
  estimate: EstimateResult;
  /** 標準納期が要相談なら null */
  delivery: DeliverySchedule | null;
  /** 依頼ごとに発行するキー。応答を受け取れずに再送しても、サーバーが同じ依頼として扱い重複して投稿しない */
  idempotencyKey: string;
}

const toSnakeRows = (
  rows: PropertyLocationRow[]
): Array<Record<string, string>> =>
  rows.map((row) => ({
    pref_name: row.prefName,
    location: row.location,
    chiban_and_kaoku_number: row.chibanAndKaokuNumber,
  }));

const trimRows = (rows: PropertyLocationRow[]): PropertyLocationRow[] =>
  getCompletePropertyRows(rows).map((row) => ({
    prefName: row.prefName.trim(),
    location: row.location.trim(),
    chibanAndKaokuNumber: row.chibanAndKaokuNumber.trim(),
  }));

/**
 * 送る内容だけにそろえる。入力途中の行・空欄の資料・前後の空白と、画面に隠れて残った値
 * （郵送しないときの郵送先、外した登記簿の隣接地、該当なしにした家屋番号）を除く。
 * 再送キーの計算と API への送信の両方に使い、送る内容が同じなら同じ再送キーになるようにする。
 */
export const normalizeResearchRequestData = (
  data: ResearchRequestFormData
): ResearchRequestFormData => {
  const hasRegistryAdjacent =
    data.selectedDocuments.includes("registry") && data.registryAdjacent;
  return {
    ...data,
    // サーバーは都道府県と住所をつないで通知するため、住所の先頭の都道府県を除く
    address: removePrefectureFromAddress(data.addressPrefecture, data.address),
    chibanList: trimRows(data.chibanList),
    kaokuNumberList: data.noKaokuNumber ? [] : trimRows(data.kaokuNumberList),
    ownerName: data.ownerName.trim(),
    // 選んだ順に左右されないよう、資料は一覧の並び順にする
    selectedDocuments: documentTypeOrder.filter((type) =>
      data.selectedDocuments.includes(type)
    ),
    registryAdjacent: hasRegistryAdjacent,
    registryAdjacentKind: hasRegistryAdjacent ? data.registryAdjacentKind : "",
    customDocuments: data.customDocuments
      .map((name) => name.trim())
      .filter((name) => name !== ""),
    requesterName: data.requesterName.trim(),
    requesterEmail: data.requesterEmail.trim(),
    requesterPhone: data.requesterPhone.trim(),
    // 短縮と日付の指定はどちらか一方だけを送る
    desiredDeliveryDate: data.specifyDeliveryDate
      ? data.desiredDeliveryDate
      : "",
    deadlineShortening: data.specifyDeliveryDate ? 0 : data.deadlineShortening,
    postalAddress: data.needsPostalDelivery ? data.postalAddress.trim() : "",
    notes: data.notes.trim(),
  };
};

/**
 * 依頼の項目を API の multipart 形式に組み立てる。
 * 一覧と見積は JSON 文字列で送り、値の無い任意項目（無効にした特別加算・希望納期）は送らない。
 * 納期短縮は、日付を指定したときもその日に間に合わせるのに要る日数を送る（見積の加算率と合わせる）。
 */
export const buildResearchRequestFormData = ({
  data: rawData,
  files,
  estimate,
  delivery,
  idempotencyKey,
}: SubmitInput): FormData => {
  const data = normalizeResearchRequestData(rawData);
  const { specialOptions } = data;
  const fields: Record<string, string | number | boolean | undefined> = {
    address_prefecture: data.addressPrefecture,
    address: data.address,
    chiban_list: JSON.stringify(toSnakeRows(data.chibanList)),
    kaoku_number_list: JSON.stringify(toSnakeRows(data.kaokuNumberList)),
    no_kaoku_number: data.noKaokuNumber,
    is_island: data.isIsland,
    owner_name: data.ownerName,
    request_types: JSON.stringify(data.requestTypes),
    onsite_photo: data.onsitePhoto,
    site_area_sqm: specialOptions.siteAreaSqm,
    subdivision_units: specialOptions.subdivisionUnits,
    condominium_units: specialOptions.condominiumUnits,
    registered_lots: specialOptions.registeredLots,
    selected_documents: JSON.stringify(data.selectedDocuments),
    registry_adjacent: data.registryAdjacent,
    registry_adjacent_kind: data.registryAdjacentKind || undefined,
    custom_documents: JSON.stringify(data.customDocuments),
    desired_delivery_date: data.desiredDeliveryDate || undefined,
    deadline_shortening: delivery?.shortening ?? 0,
    requester_name: data.requesterName,
    requester_email: data.requesterEmail,
    requester_phone: data.requesterPhone,
    needs_postal_delivery: data.needsPostalDelivery,
    postal_address: data.postalAddress,
    notes: data.notes,
    idempotency_key: idempotencyKey,
    estimate: JSON.stringify({
      area: estimate.area,
      standard_delivery: estimate.standardDelivery,
      base_price: estimate.basePrice,
      base_price_items: estimate.basePriceItems.map((item) => ({
        request_type: item.requestType,
        base_price: item.basePrice,
      })),
      shortening_rate: estimate.shorteningRate,
      shortening_amount: estimate.shorteningAmount,
      special_addition_rate: estimate.specialAdditionRate,
      special_addition_amount: estimate.specialAdditionAmount,
      remote_area_fee: estimate.remoteAreaFee,
      vehicle_fee: estimate.vehicleFee,
      access: {
        judged: estimate.access.judged,
        basis: estimate.access.basis,
        station_name: estimate.access.stationName,
        line_name: estimate.access.lineName,
        on_foot: estimate.access.onFoot,
        remote_area: estimate.access.remoteArea,
        vehicle_required: estimate.access.vehicleRequired,
      },
      postal_fee: estimate.postalFee,
      standard_delivery_date: delivery
        ? format(delivery.standardDate, "yyyy-MM-dd")
        : null,
      expected_delivery_date: delivery?.expectedDate
        ? format(delivery.expectedDate, "yyyy-MM-dd")
        : null,
      delivery_quote_required: estimate.deliveryQuoteRequired,
      total: estimate.total,
    }),
  };

  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined) formData.append(key, String(value));
  });
  files.forEach((file) => {
    formData.append("related_files", file);
  });
  return formData;
};

/** 最寄駅の判定に送る地番の上限（API と同じ）。1件の物件の地番は隣り合うため、先頭の分で足りる */
export const MAX_ACCESS_ROWS = 20;

/** 最寄駅の判定に送る対象住所の上限（API と同じ。依頼の都道府県10文字と住所500文字の合計） */
export const MAX_ACCESS_ADDRESS_LENGTH = 10 + 500;

export class RealEstateResearchRequestAPI {
  constructor(private readonly apiClient: AxiosInstance) {}

  /**
   * 地番の土地（見つからなければ対象住所の位置）から最寄駅を引き、僻地加算・車両必須加算に当たるかを判定する。
   * 応答のキーは apiClient のミドルウェアが camelCase に変える
   */
  async judgeAccess(
    rows: PropertyLocationRow[],
    address: string
  ): Promise<ResearchAccess> {
    const response = await this.apiClient.post<ResearchAccess>(
      "real-estate-research-request/access/",
      { chiban_list: toSnakeRows(rows), address }
    );
    const { data } = response;
    return {
      judged: data.judged,
      basis: data.basis,
      stationName: data.stationName,
      lineName: data.lineName,
      onFoot: data.onFoot,
      remoteArea: data.remoteArea,
      vehicleRequired: data.vehicleRequired,
    };
  }

  async submitRequest(
    input: SubmitInput
  ): Promise<RealEstateResearchRequestResponse> {
    const response =
      await this.apiClient.post<RealEstateResearchRequestResponse>(
        "real-estate-research-request/",
        buildResearchRequestFormData(input),
        { headers: { "Content-Type": "multipart/form-data" } }
      );
    return response.data;
  }
}
